import React from 'react'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import FileCopyIcon from '@material-ui/icons/FileCopy'

import { UploadButton, snackbar } from 'components'
import CustomTextField from 'components/CustomTextField'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'

export type TagLoadMode = 'csv' | 'subscription'

interface TagAssignmentOptionsProps {
  loadMode: TagLoadMode
  onLoadModeChange: (mode: TagLoadMode) => void
  file: File | null
  fileError: string | null
  onFileChange: (file: File) => void
  subscriptionDomainRestricted: boolean
  onSubscriptionDomainRestrictedChange: () => void
  subscriptionRequiredDomain: string
  onSubscriptionRequiredDomainChange: React.ChangeEventHandler<HTMLInputElement>
  // eslint-disable-next-line react/require-default-props
  subscriptionToken?: string | null
  // eslint-disable-next-line react/require-default-props
  onGenerateSubscriptionForm?: () => void
  // eslint-disable-next-line react/require-default-props
  isGeneratingForm?: boolean
  // eslint-disable-next-line react/require-default-props
  isLoading?: boolean
  // eslint-disable-next-line react/require-default-props
  csvLabel?: string
}

const getSubscriptionUrl = (token: string): string => {
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '')
  return `${base}/join/${token}`
}

const TagAssignmentOptions: React.FC<TagAssignmentOptionsProps> = ({
  loadMode,
  onLoadModeChange,
  file,
  fileError,
  onFileChange,
  subscriptionDomainRestricted,
  onSubscriptionDomainRestrictedChange,
  subscriptionRequiredDomain,
  onSubscriptionRequiredDomainChange,
  subscriptionToken,
  onGenerateSubscriptionForm,
  isGeneratingForm = false,
  isLoading = false,
  csvLabel = 'Cargar archivo CSV',
}) => {
  const handleCopyUrl = async () => {
    if (!subscriptionToken) return
    try {
      await navigator.clipboard.writeText(getSubscriptionUrl(subscriptionToken))
      snackbar.show('URL copiada al portapapeles')
    } catch {
      snackbar.show('No se pudo copiar la URL')
    }
  }

  return (
    <>
      <Grid item xs={11}>
        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
          Método de asignación
        </Typography>
        <RadioGroup
          row
          value={loadMode}
          onChange={e => onLoadModeChange(e.target.value as TagLoadMode)}
        >
          <FormControlLabel
            value="csv"
            control={<Radio color="primary" disabled={isLoading} />}
            label="Carga masiva (CSV)"
          />
          <FormControlLabel
            value="subscription"
            control={<Radio color="primary" disabled={isLoading} />}
            label="Formulario de suscripción (Web)"
          />
        </RadioGroup>
      </Grid>

      {loadMode === 'csv' && (
        <Grid item xs={11}>
          <UploadButton id="csv-upload" label={csvLabel} accept=".csv" onChange={onFileChange} />
          {fileError && (
            <Typography color="error" variant="body2">
              {fileError}
            </Typography>
          )}
          {file && !fileError && (
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Archivo seleccionado: {file.name}
            </Typography>
          )}
        </Grid>
      )}

      {loadMode === 'subscription' && (
        <>
          <Grid item xs={11}>
            <CustomFormControlLabel
              control={
                <CustomCheckbox
                  onChange={onSubscriptionDomainRestrictedChange}
                  checked={subscriptionDomainRestricted}
                  disabled={isLoading}
                />
              }
              label="Restringir por dominio de email"
            />
          </Grid>
          {subscriptionDomainRestricted && (
            <Grid item xs={11}>
              <CustomTextField
                label="Dominio requerido"
                value={subscriptionRequiredDomain}
                fullWidth
                onChange={onSubscriptionRequiredDomainChange}
                disabled={isLoading}
                placeholder="arcor.com.ar"
                helperText="Ejemplo: arcor.com.ar o @arcor.com.ar"
              />
            </Grid>
          )}
          {subscriptionToken ? (
            <Grid item xs={11}>
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                URL del formulario:
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CustomTextField value={getSubscriptionUrl(subscriptionToken)} fullWidth disabled />
                <IconButton onClick={handleCopyUrl} aria-label="Copiar URL">
                  <FileCopyIcon />
                </IconButton>
              </div>
            </Grid>
          ) : (
            onGenerateSubscriptionForm && (
              <Grid item xs={11}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={onGenerateSubscriptionForm}
                  disabled={isLoading || isGeneratingForm}
                >
                  Generar formulario de suscripción
                </Button>
              </Grid>
            )
          )}
        </>
      )}
    </>
  )
}

export { TagAssignmentOptions, getSubscriptionUrl }
